/**
 * UpdateNotification - Shows update prompt when new version is available
 * Handles service worker updates and cache refresh
 */
import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, X, Download, Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';

const UpdateNotification = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [registration, setRegistration] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Listen for service worker updates
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    // GUARD: Only register in production. In dev the webpack-dev-server
    // continuously re-installs the SW, which produced stale caches and
    // infinite reload loops via `controllerchange`. Matches `index.js`.
    if (process.env.NODE_ENV !== 'production') {
      console.log('[Update] Skipping SW registration — non-production build');
      return;
    }

    // Track the initial controller so we know when a *genuine* update swap
    // happens vs. the first-ever activation on a fresh page load. Without
    // this guard the app can spin in an endless reload loop:
    //   fresh load → SW installs → SW_ACTIVATED fires → reload() → repeat.
    const initialController = navigator.serviceWorker.controller;
    const RELOAD_MARK = 'sw-reload-mark';
    const sessionMark = sessionStorage.getItem(RELOAD_MARK);

    const safeReload = (version) => {
      if (updating) return;
      // Only reload once per (session, version) pair
      const stamp = version || 'unknown';
      if (sessionMark === stamp) {
        console.log('[Update] Reload already applied for', stamp, '— skipping to avoid loop');
        return;
      }
      sessionStorage.setItem(RELOAD_MARK, stamp);
      window.location.reload();
    };

    const handleControllerChange = () => {
      // Skip the FIRST controller assignment (initial install on a page
      // with no prior SW) — that's not an update, that's just a fresh load.
      if (!initialController) return;
      safeReload('controller-change');
    };

    const handleMessage = (event) => {
      if (event.data && event.data.type === 'SW_ACTIVATED') {
        console.log('[Update] New version activated:', event.data.version);
        // Same guard — don't reload on the very first activation of a
        // fresh page. Only reload when we already had a controller (i.e.
        // the user was mid-session when the new SW took over).
        if (!initialController) return;
        safeReload(event.data.version);
      }
    };

    // Register service worker and listen for updates
    navigator.serviceWorker.register('/service-worker.js')
      .then((reg) => {
        setRegistration(reg);
        console.log('[Update] Service worker registered');

        // Check for updates on registration
        reg.addEventListener('updatefound', () => {
          const newWorker = reg.installing;
          if (!newWorker) return;

          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // New version available
              console.log('[Update] New version available');
              setUpdateAvailable(true);
            }
          });
        });

        // Check for updates periodically (every 5 minutes)
        const updateInterval = setInterval(() => {
          reg.update().catch(console.error);
        }, 5 * 60 * 1000);

        return () => clearInterval(updateInterval);
      })
      .catch((error) => {
        console.error('[Update] Service worker registration failed:', error);
      });

    // Listen for controller changes
    navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);
    navigator.serviceWorker.addEventListener('message', handleMessage);

    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
      navigator.serviceWorker.removeEventListener('message', handleMessage);
    };
  }, [updating]);

  // Handle update action
  const handleUpdate = useCallback(() => {
    if (!registration || !registration.waiting) return;

    setUpdating(true);
    
    // Tell waiting service worker to skip waiting
    registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    
    // Reload after a short delay
    setTimeout(() => {
      window.location.reload();
    }, 500);
  }, [registration]);

  // Handle dismiss
  const handleDismiss = () => {
    setDismissed(true);
    // Show again after 30 minutes
    setTimeout(() => setDismissed(false), 30 * 60 * 1000);
  };

  // Force refresh (clear cache and reload)
  const handleForceRefresh = useCallback(async () => {
    setUpdating(true);
    
    try {
      // Clear all caches
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }
      
      // Unregister service worker and reload
      if (registration) {
        await registration.unregister();
      }
      
      // Hard reload
      window.location.reload(true);
    } catch (error) {
      console.error('[Update] Force refresh failed:', error);
      window.location.reload();
    }
  }, [registration]);

  if (!updateAvailable || dismissed) return null;

  return (
    <div 
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-in slide-in-from-bottom-5 duration-300"
      data-testid="update-notification"
    >
      <div className="bg-slate-900 text-white rounded-lg shadow-2xl border border-slate-700 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-3 bg-indigo-600">
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4" />
            <span className="font-semibold text-sm">Update Available</span>
          </div>
          <button 
            onClick={handleDismiss}
            className="text-white/80 hover:text-white p-1 rounded"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-4">
          <p className="text-sm text-slate-300 mb-4">
            A new version of the app is available. Refresh to get the latest features and fixes.
          </p>
          
          <div className="flex gap-2">
            <Button
              onClick={handleUpdate}
              disabled={updating}
              className="flex-1 bg-indigo-600 hover:bg-indigo-700"
              size="sm"
            >
              {updating ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Update Now
                </>
              )}
            </Button>
            
            <Button
              onClick={handleForceRefresh}
              disabled={updating}
              variant="outline"
              size="sm"
              className="border-slate-600 text-slate-300 hover:bg-slate-800"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateNotification;
